import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import konkrApi from '../../api/konkrApi';
import { ADVERTISER_STATUSES, OFFER_STATUSES, PARTNERSHIPS, formatKonkrInstant, konkrError, partnershipLabel } from '../../utils/konkr';
import KonkrEditor from './KonkrEditor';
import './Konkr.css';

const KonkrManagement = ({ kind, onAccessError }) => {
  const navigate = useNavigate();
  const isOffer = kind === 'offers';
  const title = isOffer ? 'Offers' : 'Advertisers';
  const [filters, setFilters] = useState({});
  const [applied, setApplied] = useState({});
  const [version, setVersion] = useState(0);
  const [data, setData] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);
  const [first, setFirst] = useState(0);
  const generation = useRef(0);
  const token = localStorage.getItem('token');
  useEffect(() => {
    const request = ++generation.current;
    let current = true;
    setLoading(true); setError(''); setData([]);
    const load = async () => {
      try {
        const [rows, options] = await Promise.all([konkrApi.list(kind, applied),
          isOffer ? konkrApi.list('advertisers') : Promise.resolve([])]);
        if (!current || request !== generation.current || token !== localStorage.getItem('token')) return;
        setData(rows); setAdvertisers(options);
      } catch (failure) {
        if (!current || request !== generation.current || token !== localStorage.getItem('token')) return;
        if (!onAccessError(failure)) setError(konkrError(failure, `Unable to load Konkr ${kind}.`));
      } finally {
        if (current && request === generation.current && token === localStorage.getItem('token')) setLoading(false);
      }
    };
    load();
    return () => { current = false; };
  }, [kind, isOffer, applied, version, onAccessError, token]);
  const refresh = () => { setFirst(0); setVersion(value => value + 1); };
  const updateFilter = (key, value) => setFilters(previous => ({ ...previous, [key]: value }));
  const dropdown = (key, label, options) => <div className="field">
    <label htmlFor={`konkr-filter-${key}`}>{label}</label>
    <Dropdown inputId={`konkr-filter-${key}`} value={filters[key] || null} options={options} showClear placeholder="All"
      onChange={event => updateFilter(key, event.value)} disabled={Boolean(editor)} />
  </div>;
  const statusOptions = (isOffer ? OFFER_STATUSES : ADVERTISER_STATUSES).map(value => ({ label: value, value }));
  return <Card className="konkr-area mt-5 mx-3 mx-md-5"><Container fluid="xl">
    <div className="flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
      <div><h4>Konkr Rewards — {title}</h4><small>Konkr User · Rewards administration</small></div>
      <div className="flex flex-wrap gap-2">
        <Button label="Advertisers" className={isOffer ? 'p-button-text' : 'p-button-outlined'} disabled={Boolean(editor)} onClick={() => navigate('/support/konkr/advertisers')} />
        <Button label="Offers" className={isOffer ? 'p-button-outlined' : 'p-button-text'} disabled={Boolean(editor)} onClick={() => navigate('/support/konkr/offers')} />
        <Button label={`Create ${isOffer ? 'Offer' : 'Advertiser'}`} icon="pi pi-plus" disabled={loading || Boolean(error) || Boolean(editor)} onClick={() => setEditor({ id: null })} />
        <Button aria-label={`Refresh ${kind}`} icon="pi pi-refresh" className="p-button-text" disabled={loading || Boolean(editor)} onClick={refresh} />
      </div>
    </div>
    <form className="konkr-filters p-fluid mb-4" onSubmit={event => { event.preventDefault(); setFirst(0); setApplied({ ...filters }); }}>
      {!isOffer && <div className="field"><label htmlFor="konkr-search">Search advertisers</label>
        <InputText id="konkr-search" value={filters.search || ''} disabled={Boolean(editor)} onChange={event => updateFilter('search', event.target.value)} />
      </div>}
      {isOffer && dropdown('advertiserId', 'Advertiser', advertisers.map(item => ({ label: item.name, value: item.id })))}
      {dropdown('status', isOffer ? 'Offer status' : 'Konkr status', statusOptions)}
      {!isOffer && dropdown('partnershipStatus', 'Awin partnership', PARTNERSHIPS.map(value => ({ label: partnershipLabel(value), value })))}
      <div className="flex gap-2 align-items-end mb-3">
        <Button label="Apply Filters" type="submit" disabled={loading || Boolean(editor)} />
        <Button label="Clear Filters" type="button" className="p-button-text" disabled={Boolean(editor)} onClick={() => { setFilters({}); setApplied({}); setFirst(0); }} />
      </div>
    </form>
    {loading && <div role="status" aria-label={`Loading Konkr ${kind}`}><ProgressBar mode="indeterminate" style={{ height: '6px' }} /></div>}
    {error && <div className="flex align-items-center gap-2 mb-3"><Message severity="error" text={error} /><Button label="Retry" onClick={refresh} /></div>}
    {!loading && !error && <DataTable value={data} dataKey="id" responsiveLayout="scroll" paginator rows={20} first={first}
      onPage={event => setFirst(event.first)} emptyMessage={`No matching ${kind}.`} rowsPerPageOptions={[20, 50, 100]}>
      {isOffer ? [
        <Column key="advertiser" field="advertiserName" header="Advertiser" />,
        <Column key="title" header="Title" body={row => row.title || 'Untitled draft'} />,
        <Column key="status" field="status" header="Status" body={row => <Tag value={row.status} />} />,
        <Column key="points" field="pointsRequired" header="Points required" />,
        <Column key="featured" header="Featured" body={row => row.featured ? 'Yes' : 'No'} />,
        <Column key="start" header="Start (London)" body={row => formatKonkrInstant(row.startDate)} />,
        <Column key="expiry" header="Expiry (London)" body={row => formatKonkrInstant(row.expiryDate)} />,
        <Column key="live" header="Availability" body={row => <Tag value={row.isLive ? 'Live' : 'Not live'} severity={row.isLive ? 'success' : 'warning'} />} />,
      ] : [
        <Column key="name" field="name" header="Name" />,
        <Column key="awinId" field="awinAdvertiserId" header="Awin advertiser ID" />,
        <Column key="partnership" header="Awin partnership" body={row => <Tag value={partnershipLabel(row.awinPartnershipStatus)} severity={row.awinPartnershipStatus === 'Approved' ? 'success' : 'warning'} />} />,
        <Column key="status" header="Konkr status" body={row => <Tag value={row.status} severity={row.status === 'Active' ? 'success' : 'warning'} />} />,
        <Column key="count" field="offerCount" header="Offer count" />,
      ]}
      <Column header="Actions" body={row => <div className="flex flex-wrap gap-2">
        <Button label="Edit" aria-label={`Edit ${isOffer ? row.title || 'untitled offer' : row.name}`} icon="pi pi-pencil"
          className="p-button-text p-button-sm" onClick={() => setEditor({ id: row.id })} />
        {!isOffer && <Button label={row.status === 'Active' ? 'Deactivate' : 'Activate'} className="p-button-text p-button-sm"
          onClick={() => setEditor({ id: row.id, initialAction: row.status === 'Active' ? 'deactivate' : 'activate' })} />}
      </div>} />
    </DataTable>}
    {editor && <KonkrEditor key={`${kind}:${editor.id || 'new'}`} kind={kind} {...editor} advertisers={advertisers}
      onAccessError={onAccessError} onChanged={refresh} onClose={() => { setEditor(null); refresh(); }} />}
  </Container></Card>;
};
export default KonkrManagement;
